"use client";
import { useEffect, useState, Suspense, useMemo } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { ProjectCard } from "@/components/ProjectCard";
import { Button } from "@/components/ui/button";
import { Project } from "@/lib/schema";
import { useProjects } from "@/hooks/useProjects";
import { PlusCircle, Target, BookOpen, FileText, Info, Search, Headphones, MessageCircle, Table, Users, X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useSubscription, isProjectNearLimit, isProjectAtLimit, getProjectUsagePercentage } from "@/hooks/useSubscription";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Progress } from "@/components/ui/progress";
import LoadingIndicator from "@/components/utils/Loading";
import { ProjectInvitations } from "@/components/ProjectInvitations";

type ProjectFilter = "hasAudio" | "hasChats" | "hasDataTables" | "shared";

const FILTER_CONFIG: Record<ProjectFilter, { label: string; icon: React.ElementType; check: (p: Project) => boolean }> = {
	hasAudio: { label: "Audio Overviews", icon: Headphones, check: (p) => (p.num_audio_overviews ?? 0) > 0 },
	hasChats: { label: "Chats", icon: MessageCircle, check: (p) => (p.num_conversations ?? 0) > 0 },
	hasDataTables: { label: "Data Tables", icon: Table, check: (p) => (p.num_data_tables ?? 0) > 0 },
	shared: { label: "Shared", icon: Users, check: (p) => (p.num_roles ?? 1) > 1 },
};

function ProjectsPage() {
	const { projects, isLoading, error, refetch: getProjects } = useProjects(true);
	const { user, loading: userLoading } = useAuth();
	const { subscription } = useSubscription();
	const router = useRouter();
	const searchParams = useSearchParams();

	const [showUsageAlert, setShowUsageAlert] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [activeFilters, setActiveFilters] = useState<Set<ProjectFilter>>(new Set());

	const atProjectLimit = subscription ? isProjectAtLimit(subscription) : false;
	const nearProjectLimit = subscription ? isProjectNearLimit(subscription) : false;
	const openInvites = searchParams.get("openInvites") !== null;

	const toggleFilter = (filter: ProjectFilter) => {
		setActiveFilters((prev) => {
			const next = new Set(prev);
			if (next.has(filter)) {
				next.delete(filter);
			} else {
				next.add(filter);
			}
			return next;
		});
	};

	const clearAllFilters = () => {
		setSearchQuery("");
		setActiveFilters(new Set());
	};

	const filteredProjects = useMemo(() => {
		return projects.filter((project) => {
			// Search filter
			if (searchQuery.trim()) {
				const query = searchQuery.toLowerCase();
				const matchesTitle = project.title.toLowerCase().includes(query);
				const matchesDescription = project.description?.toLowerCase().includes(query);
				if (!matchesTitle && !matchesDescription) return false;
			}

			// Quick filters - all active filters must match
			for (const filter of activeFilters) {
				if (!FILTER_CONFIG[filter].check(project)) return false;
			}

			return true;
		});
	}, [projects, searchQuery, activeFilters]);

	const hasActiveFilters = searchQuery.trim() !== "" || activeFilters.size > 0;

	useEffect(() => {
		const PROJECT_LIMIT_TOAST_KEY = "project_limit_toast_shown";

		if (subscription) {
			// Only show toast once per session
			if (sessionStorage.getItem(PROJECT_LIMIT_TOAST_KEY)) {
				return;
			}

			let toastShown = false;

			if (atProjectLimit) {
				toast.error("Project limit reached", {
					description: "You've used your available project slots. Upgrade for more, or archive existing projects.",
					action: {
						label: "Upgrade",
						onClick: () => router.push("/pricing"),
					},
				});
				toastShown = true;
			} else if (nearProjectLimit) {
				toast.warning("Approaching project limit", {
					description: "You're getting close to your project limit.",
					action: {
						label: "Upgrade",
						onClick: () => router.push("/pricing"),
					},
				});
				toastShown = true;
			}

			if (toastShown) {
				sessionStorage.setItem(PROJECT_LIMIT_TOAST_KEY, "true");
			}
		}
	}, [atProjectLimit, nearProjectLimit, subscription, router]);

	// Empty state component
	const EmptyState = () => (
		<div className="flex flex-col items-center justify-center py-16 px-4 text-center max-w-2xl mx-auto min-h-[60vh]">
			<h2 className="text-2xl font-bold text-foreground mb-3">
				Organize Your Research
			</h2>
			<p className="text-muted-foreground mb-8 max-w-md">
				Group papers by topic, get focused AI assistance, and streamline your literature reviews.
			</p>

			{/* CTA buttons */}
			<div className="flex flex-col sm:flex-row gap-3 mb-12">
				{atProjectLimit ? (
					<Button
						size="lg"
						className="bg-primary hover:bg-primary/90"
						disabled
					>
						<PlusCircle className="mr-2 h-4 w-4" />
						Create your first project
					</Button>
				) : (
					<Button
						asChild
						size="lg"
						className="bg-primary hover:bg-primary/90"
					>
						<Link href="/projects/create">
							<PlusCircle className="mr-2 h-4 w-4" />
							Create your first project
						</Link>
					</Button>
				)}
				<Button variant="outline" size="lg" asChild>
					<Link href="/blog/projects">
						Learn more
					</Link>
				</Button>
			</div>

			{/* Feature highlights */}
			<div className="flex items-center justify-center gap-8 text-blue-500">
				<div className="flex flex-col items-center gap-1 max-w-24">
					<Target className="h-5 w-5" />
					<span className="text-xs font-medium text-foreground">Focus</span>
					<span className="text-xs text-muted-foreground text-center">Organize by topic or goal</span>
				</div>
				<div className="flex flex-col items-center gap-1 max-w-24">
					<BookOpen className="h-5 w-5" />
					<span className="text-xs font-medium text-foreground">AI Chat</span>
					<span className="text-xs text-muted-foreground text-center">Context-aware assistance</span>
				</div>
				<div className="flex flex-col items-center gap-1 max-w-24">
					<FileText className="h-5 w-5" />
					<span className="text-xs font-medium text-foreground">Research</span>
					<span className="text-xs text-muted-foreground text-center">Streamline lit reviews</span>
				</div>
			</div>
		</div>
	);

	return (
		// One centered column — the list, header, and controls share the same width.
		<div className="mx-auto w-full max-w-4xl p-4">
			{(nearProjectLimit || atProjectLimit) && subscription && showUsageAlert && (
				<Alert variant={'default'} className="mb-4 border-muted">
					<div className="flex justify-between items-start">
						<div className="flex items-start">
							<Info className="h-4 w-4 mt-1 text-muted-foreground" />
							<div className="ml-2">
								<AlertTitle className="text-foreground">Usage summary</AlertTitle>
								<AlertDescription className="text-muted-foreground">
									{atProjectLimit
										? `You've used your available project slots. Upgrade for more, or archive existing projects.`
										: `You're getting close to your project limit.`}
								</AlertDescription>
							</div>
						</div>
						<div className="flex items-center gap-x-2">
							<Button asChild size="sm" variant="outline">
								<Link href="/pricing">View plans</Link>
							</Button>
							<Button variant="ghost" size="sm" onClick={() => setShowUsageAlert(false)} className="self-start">
								Dismiss
							</Button>
						</div>
					</div>
					<div className="mt-4 space-y-4">
						<div>
							<div className="flex justify-between text-sm text-muted-foreground">
								<span>Projects: {subscription.usage.projects} used</span>
								<span>{subscription.limits.projects} total</span>
							</div>
							<Progress value={getProjectUsagePercentage(subscription)} className="h-2 mt-1" />
						</div>
					</div>
				</Alert>
			)}

			<div className="flex justify-between items-center mb-4">
				<h1 className="text-2xl font-bold">Projects</h1>
				<div className="flex gap-2">
					<ProjectInvitations onInvitationAccepted={getProjects} defaultOpen={openInvites} />
					{projects.length > 0 && (
						atProjectLimit ? (
							<Button className="bg-blue-500 dark:text-card-foreground hover:bg-blue-600 dark:hover:bg-blue-400" disabled>
								<PlusCircle className="mr-2" />
								New Project
							</Button>
						) : (
							<Button asChild className="bg-blue-500 dark:text-card-foreground hover:bg-blue-600 dark:hover:bg-blue-400">
								<Link href="/projects/create">
									<PlusCircle className="mr-2" />
									New Project
								</Link>
							</Button>
						)
					)}
				</div>
			</div>

			{/* Search and Filters — one compact row */}
			{projects.length > 0 && (
				<div className="mb-4 flex flex-wrap items-center gap-2">
					<div className="relative min-w-48 flex-1 sm:max-w-xs">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							type="text"
							placeholder="Search projects..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="h-8 pl-9"
						/>
					</div>
					{(Object.keys(FILTER_CONFIG) as ProjectFilter[]).map((filterKey) => {
						const config = FILTER_CONFIG[filterKey];
						const Icon = config.icon;
						const isActive = activeFilters.has(filterKey);
						return (
							<button
								key={filterKey}
								onClick={() => toggleFilter(filterKey)}
								className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
									isActive
										? "bg-primary text-primary-foreground"
										: "bg-secondary text-secondary-foreground hover:bg-secondary/80"
								}`}
							>
								<Icon className="h-3.5 w-3.5" />
								{config.label}
							</button>
						);
					})}
					{hasActiveFilters && (
						<>
							<button
								onClick={clearAllFilters}
								className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
							>
								<X className="h-3.5 w-3.5" />
								Clear
							</button>
							<span className="text-xs text-muted-foreground">
								{filteredProjects.length} of {projects.length} project{projects.length !== 1 ? "s" : ""}
							</span>
						</>
					)}
				</div>
			)}
			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<LoadingIndicator />
					<span className="ml-3 text-gray-600">Retrieving your projects...</span>
				</div>
			) : error ? (
				<div className="flex flex-col items-center justify-center py-12">
					<p className="text-red-500 mb-4">Failed to fetch projects. Please try again.</p>
					<Button onClick={getProjects} variant="outline">
						Try Again
					</Button>
				</div>
			) : projects.length === 0 ? (
				<EmptyState />
			) : filteredProjects.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
						<Search className="w-8 h-8 text-muted-foreground" />
					</div>
					<h3 className="text-lg font-semibold mb-2">No projects match your filters</h3>
					<p className="text-muted-foreground mb-4 max-w-md">
						{searchQuery.trim()
							? `No projects found matching "${searchQuery}"`
							: "No projects match the selected filters"}
					</p>
					<Button variant="outline" onClick={clearAllFilters}>
						<X className="mr-2 h-4 w-4" />
						Clear Filters
					</Button>
				</div>
			) : (
				<div className="flex flex-col divide-y divide-border/60">
					{/* New Project row */}
					{!hasActiveFilters && (
						atProjectLimit ? (
							<div className="flex cursor-not-allowed items-center gap-3 px-3 py-2.5 text-muted-foreground/60">
								<Plus className="h-4 w-4" aria-hidden />
								<span className="text-sm font-medium">New Project</span>
								<span className="text-xs">Upgrade to create more</span>
							</div>
						) : (
							<Link
								href="/projects/create"
								className="flex items-center gap-3 px-3 py-2.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
							>
								<Plus className="h-4 w-4" aria-hidden />
								<span className="text-sm font-medium">New Project</span>
							</Link>
						)
					)}
					{filteredProjects.map((project) => (
						<ProjectCard key={project.id} project={project} onProjectUpdate={getProjects} />
					))}
				</div>
			)}
		</div>
	);
}

export default function Projects() {
	return (
		<Suspense fallback={<div className="flex items-center justify-center py-12">
			<LoadingIndicator />
			<span className="ml-3 text-gray-600">Loading...</span>
		</div>}>
			<ProjectsPage />
		</Suspense>
	)
}
